import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  Devices as DevicesIcon,
  DirectionsCar as VehicleIcon,
  Description as DocumentIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Schedule as PendingIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { clientsAPI, devicesAPI, vehiclesAPI, documentsAPI } from '../services/api';

function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    clients: 0,
    devices: 0,
    vehicles: 0,
    documents: 0,
    pendingDocuments: 0,
    paidInvoices: 0,
    unpaidInvoices: 0,
  });
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [clientsRes, devicesRes, vehiclesRes, documentsRes] = await Promise.all([
        clientsAPI.getAll(),
        devicesAPI.getAllBulk(),
        vehiclesAPI.getAll(),
        documentsAPI.getAll(),
      ]);

      const docs = documentsRes.data || [];
      const clients = clientsRes.data || [];
      
      // Calculate stats
      const pendingDocs = docs.filter(d => d.status === 'pending_client').length;
      const paidInvoices = docs.filter(d => 
        d.document_type === 'invoice' && d.invoice_details?.payment_status === 'paid'
      ).length;
      const unpaidInvoices = docs.filter(d => 
        d.document_type === 'invoice' && d.invoice_details?.payment_status !== 'paid'
      ).length;

      setStats({
        clients: clients.length,
        devices: devicesRes.data.length,
        vehicles: vehiclesRes.data.length,
        documents: docs.length,
        pendingDocuments: pendingDocs,
        paidInvoices,
        unpaidInvoices,
      });
      
      // Recent documents (last 5)
      setRecentDocuments(docs.slice(0, 5));
      
      // Recent clients (last 5)
      setRecentClients(clients.slice(0, 5));
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        } : {},
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5, fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
              {loading ? <CircularProgress size={32} sx={{ color: 'white' }} /> : value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box 
            sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const getDocTypeColor = (type) => {
    switch (type) {
      case 'invoice': return 'success';
      case 'proforma': return 'warning';
      case 'attestation': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'validated': return <CheckIcon fontSize="small" color="success" />;
      case 'pending_client': return <PendingIcon fontSize="small" color="warning" />;
      case 'draft': return <WarningIcon fontSize="small" color="disabled" />;
      default: return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Tableau de Bord
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bienvenue! Voici un aperçu de votre activité.
          </Typography>
        </Box>
        <IconButton onClick={loadStats} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Main Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <StatCard
            title="Clients"
            value={stats.clients}
            icon={PeopleIcon}
            color="#6366f1"
            subtitle="Total enregistrés"
            onClick={() => navigate('/clients')}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <StatCard
            title="Véhicules"
            value={stats.vehicles}
            icon={VehicleIcon}
            color="#f59e0b"
            subtitle="Flotte totale"
            onClick={() => navigate('/vehicles')}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <StatCard
            title="Dispositifs GPS"
            value={stats.devices}
            icon={DevicesIcon}
            color="#10b981"
            subtitle="Installés"
            onClick={() => navigate('/devices')}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <StatCard
            title="Documents"
            value={stats.documents}
            icon={DocumentIcon}
            color="#ec4899"
            subtitle="Total créés"
            onClick={() => navigate('/documents')}
          />
        </Box>
      </Box>

      {/* Secondary Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 280 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DocumentIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Documents</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366f1' }}>
                    {stats.documents}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                    {stats.pendingDocuments}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">En attente</Typography>
                </Box>
              </Box>
              <Button 
                fullWidth 
                variant="outlined" 
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/documents')}
              >
                Voir tous
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 280 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Paiements</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Factures payées</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {stats.paidInvoices}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.paidInvoices + stats.unpaidInvoices > 0 
                    ? (stats.paidInvoices / (stats.paidInvoices + stats.unpaidInvoices)) * 100 
                    : 0
                  } 
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  color="success"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Non payées</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {stats.unpaidInvoices}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 280 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Actions Rapides</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  fullWidth
                  onClick={() => navigate('/clients')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Nouveau Client
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<VehicleIcon />} 
                  fullWidth
                  onClick={() => navigate('/vehicles')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Ajouter Véhicule
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<ReceiptIcon />} 
                  fullWidth
                  onClick={() => navigate('/documents')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Créer Document
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recent Activity */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: '1 1 calc(60% - 12px)', minWidth: 350 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Documents Récents</Typography>
                <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/documents')}>
                  Voir tous
                </Button>
              </Box>
              <List disablePadding>
                {recentDocuments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Aucun document récent
                  </Typography>
                ) : (
                  recentDocuments.map((doc, index) => (
                    <React.Fragment key={doc.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getStatusIcon(doc.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {doc.document_number}
                              </Typography>
                              <Chip 
                                label={doc.document_type_display || doc.document_type} 
                                size="small" 
                                color={getDocTypeColor(doc.document_type)}
                              />
                            </Box>
                          }
                          secondary={`${doc.client_name || 'Client'} • ${doc.document_date}`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {doc.total_ttc ? `${parseFloat(doc.total_ttc).toLocaleString()} DZD` : '-'}
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < recentDocuments.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 calc(40% - 12px)', minWidth: 300 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Clients Récents</Typography>
                <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/clients')}>
                  Voir tous
                </Button>
              </Box>
              <List disablePadding>
                {recentClients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Aucun client récent
                  </Typography>
                ) : (
                  recentClients.map((client, index) => (
                    <React.Fragment key={client.id}>
                      <ListItem 
                        sx={{ px: 0, cursor: 'pointer' }}
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                            {client.company_name?.[0]?.toUpperCase()}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={client.company_name}
                          secondary={client.client_code}
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            label={client.is_active ? 'Actif' : 'Inactif'} 
                            size="small" 
                            color={client.is_active ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < recentClients.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default DashboardPage;
